import React, { useEffect, useState } from 'react';
import { Music, Film, Mail, Instagram, Youtube, Lock } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FanSignup from '../components/FanSignup';
import '../styles/Home.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mwp-site">
      <Toaster position="top-center" richColors />
      
      {/* Navigation */}
      <nav className="mwp-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_daa53b2c-a32e-47fc-b8be-c3729304beb0/artifacts/d3h4utx6_MWP%20Label%20Logo.png" 
              alt="Made With Purpose" 
              className="logo-image"
            />
          </div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('music')} className="nav-link">Music</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
            <button onClick={() => scrollToSection('connect')} className="nav-link">Connect</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="film-grain"></div>
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content" style={{ transform: `translateY(${scrollY * 0.3}px)` }}>
          <div className="hero-logo-large">
            <img 
              src="https://customer-assets.emergentagent.com/job_daa53b2c-a32e-47fc-b8be-c3729304beb0/artifacts/d3h4utx6_MWP%20Label%20Logo.png" 
              alt="Made With Purpose" 
            />
          </div>
          <h1 className="hero-title">MADE WITH PURPOSE</h1>
          <p className="hero-subtitle">ENTERTAINMENT WITH INTENT.</p>
          <p className="hero-tagline">Music. Film. Art. Built with meaning.</p>
          
          <div className="hero-intro">
            <p>
              Made With Purpose (MWP) is a purpose-led entertainment and media enterprise 
              focused on creating meaningful work across music, film and art. We exist to 
              develop and release projects that combine creativity with intention — building 
              culture, telling human stories and supporting ventures that matter.
            </p>
          </div>

          <div className="hero-buttons">
            <button onClick={() => scrollToSection('music')} className="btn-primary">
              <Music className="btn-icon" />
              Listen
            </button>
            <button onClick={() => scrollToSection('music')} className="btn-secondary">
              <Film className="btn-icon" />
              Watch
            </button>
            <button onClick={() => scrollToSection('connect')} className="btn-secondary">
              <Mail className="btn-icon" />
              Connect
            </button>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* Music & Video Section */}
      <section id="music" className="music-section" data-animate>
        <div className={`section-container fade-in ${isVisible['music'] ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">OLIVER SIMPSON</h2>
            <p className="section-subtitle">
              The flagship artist of Made With Purpose — blending soul, pop and cinematic 
              storytelling with emotion and meaning.
            </p>
          </div>

          <div className="media-grid">
            {/* Spotify Embed */}
            <div className="media-card spotify-card">
              <h3 className="media-title">Latest Releases</h3>
              <div className="embed-container">
                <iframe
                  style={{ borderRadius: '12px' }}
                  src="https://open.spotify.com/embed/artist/3oBjF43XGaRrIciYVRbKe8?utm_source=generator"
                  width="100%"
                  height="450"
                  frameBorder="0"
                  allowFullScreen=""
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="Oliver Simpson on Spotify"
                ></iframe>
              </div>
              <a 
                href="https://open.spotify.com/artist/3oBjF43XGaRrIciYVRbKe8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="media-link"
              >
                View Full Profile on Spotify →
              </a>
            </div>

            {/* YouTube Section - Featured Video + Channel Links */}
            <div className="media-card youtube-card">
              <h3 className="media-title">Videos</h3>
              
              {/* Featured Video Embed */}
              <div className="embed-container">
                <iframe
                  width="100%"
                  height="450"
                  src="https://www.youtube.com/embed/iL-fLlLHQio"
                  title="Oliver Simpson - Featured Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ borderRadius: '12px' }}
                ></iframe>
              </div>

              {/* Channel Action Buttons */}
              <div style={{ 
                display: 'grid', 
                gap: '12px',
                marginTop: '24px'
              }}>
                <a 
                  href="https://youtube.com/@oliversimpsonmusic/videos" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                >
                  <Youtube size={20} />
                  Watch Latest Videos
                </a>
                <a 
                  href="https://youtube.com/@oliversimpsonmusic?sub_confirmation=1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                >
                  <Youtube size={20} />
                  Subscribe on YouTube
                </a>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="social-links-section">
            <h3 className="social-title">Follow Oliver Simpson</h3>
            <div className="social-icons">
              <a 
                href="https://open.spotify.com/artist/3oBjF43XGaRrIciYVRbKe8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Spotify"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </a>
              <a 
                href="https://youtube.com/@oliversimpsonmusic" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="YouTube"
              >
                <Youtube size={24} />
              </a>
              <a 
                href="https://www.instagram.com/oliversimpsonig" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section" data-animate>
        <div className={`section-container fade-in ${isVisible['about'] ? 'visible' : ''}`}>
          <h2 className="section-title">ABOUT MADE WITH PURPOSE</h2>
          
          <div className="about-content">
            <div className="about-text">
              <p className="about-intro">
                Made With Purpose (MWP) is a purpose-driven entertainment and media enterprise 
                working across music, film and art.
              </p>
              
              <p className="about-body">
                MWP exists to develop and release projects that combine creativity with intention — 
                building culture, telling meaningful stories and supporting purpose-led ventures.
              </p>

              <div className="about-list">
                <h3 className="about-list-title">Our work spans:</h3>
                <ul>
                  <li>Music and artist development</li>
                  <li>Film and visual storytelling</li>
                  <li>Creative partnerships</li>
                  <li>Purpose-driven projects and ventures</li>
                </ul>
              </div>

              <p className="about-motto">
                Everything we create is built with purpose — not just profit.
              </p>
            </div>

            <div className="about-visual">
              <div className="purpose-badge">
                <img 
                  src="https://customer-assets.emergentagent.com/job_daa53b2c-a32e-47fc-b8be-c3729304beb0/artifacts/d3h4utx6_MWP%20Label%20Logo.png" 
                  alt="MWP" 
                />
                <p className="badge-text">Built with meaning</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fan Signup Section */}
      <FanSignup />

      {/* Connect Section */}
      <section id="connect" className="connect-section" data-animate>
        <div className={`section-container fade-in ${isVisible['connect'] ? 'visible' : ''}`}>
          <h2 className="section-title">CONNECT</h2>
          <p className="section-subtitle">Follow Oliver Simpson and Made With Purpose.</p>

          {/* Social Media */}
          <div className="connect-social">
            <h3 className="social-title">Follow Us</h3>
            <div className="social-icons-large">
              <a 
                href="https://open.spotify.com/artist/3oBjF43XGaRrIciYVRbKe8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-large"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                <span>Spotify</span>
              </a>
              <a 
                href="https://youtube.com/@oliversimpsonmusic" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-large"
              >
                <Youtube size={32} />
                <span>YouTube</span>
              </a>
              <a 
                href="https://www.instagram.com/oliversimpsonig" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-large"
              >
                <Instagram size={32} />
                <span>Instagram</span>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-container">
            <h3 className="form-title">Get in Touch</h3>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project or inquiry..."
                  required
                ></textarea>
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={isSubmitting}>
                <Mail className="btn-icon" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mwp-footer">
        <div className="footer-container">
          <div className="footer-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_daa53b2c-a32e-47fc-b8be-c3729304beb0/artifacts/d3h4utx6_MWP%20Label%20Logo.png" 
              alt="Made With Purpose" 
            />
          </div>
          <p className="footer-tagline">Entertainment with intent.</p>
          <div className="footer-social">
            <a href="https://open.spotify.com/artist/3oBjF43XGaRrIciYVRbKe8" target="_blank" rel="noopener noreferrer" aria-label="Spotify">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
            </a>
            <a href="https://youtube.com/@oliversimpsonmusic" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <Youtube size={20} />
            </a>
            <a href="https://www.instagram.com/oliversimpsonig" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={20} />
            </a>
          </div>
          <a 
            href="/admin/login" 
            className="footer-admin-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/login');
            }}
          >
            <Lock size={16} />
            Admin Login
          </a>
          <p className="footer-copyright">© 2025 Made With Purpose. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;